<template>
  <div class="bg-white w-full">
    <CustomerDrugInteractionHeader
      v-if="customerId"
      class="-mx-4"
      :consumer-key="consumerKey"
      :customer-id="customerId"
      show-vidal-analysis
      @launch-analysis="launchAnalysis"
      @track-products="$emit('track-products')"
    />
    <div class="bg-primary/15 px-4 py-4 -mx-4 space-y-4">
      <div>
        <h2>Sélectionnez les traitements du patient</h2>
        <p>
          Vous pourrez ensuite analyser les interactions médicamenteuses, contrôler l'observance, et compléter le
          pilulier du patient.
        </p>
      </div>
      <div class="flex gap-2">
        <PrescriptorFilters
          :model-value="prescriptors[0] ?? null" :consumer-key="consumerKey"
          @update:model-value="(value) => value ? prescriptors = [value] : prescriptors = []"
        />
        <base-product-search-bar
          class="flex-1"
          placeholder="+ Ajouter un produit"
          input-class="shadow-sm"
          @select="handleSearchbarSelection"
        />
      </div>
    </div>
    <div class="space-y-4 bg-gray-100 -mx-4 p-4">
      <div class="flex justify-between">
        <h1 class="text-black">
          {{ viewMode === 'raw' ? 'Délivrances' : 'Ordonnances' }}
        </h1>
        <DrugSelectionViewMode v-model:model-value="viewMode" class="shadow-md" />
      </div>
      <DrugSelectionPrescriptionView
        v-if="viewMode === 'prescriptions'"
        :consumer-key="consumerKey"
        :selected-products="selectedProducts"
        :prescriptors="prescriptors"
        :initial-carts="carts"
        :initial-carts-loading="loadingStore.carts"
        :initial-cart-after-key="cartAfterKey"
        @select="handleSelect"
      />
      <DrugSelectionRawView
        v-else
        :consumer-key="consumerKey"
        :selected-products="selectedProducts"
        :search-bar-products="searchBarProducts"
        :prescriptors="prescriptors"
        @select="handleSelect"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import {consumerProductApi} from "@/container";
import {computed, ref, watch} from "vue";
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {LegacyProduct} from "@/core/interfaces/Product";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";
import useConsumerProducts from "@/consumer/composables/useConsumerProducts";
import debounce from "lodash.debounce";
import CustomerDrugInteractionHeader from "@/drug-interaction/components/header/CustomerDrugInteractionHeader.vue";
import DrugSelectionViewMode from "@/drug-interaction/components/drug-selection/DrugSelectionViewMode.vue";
import DrugSelectionPrescriptionView
    from "@/drug-interaction/components/drug-selection/prescription/DrugSelectionPrescriptionView.vue";
import DrugSelectionRawView from "@/drug-interaction/components/drug-selection/raw/DrugSelectionRawView.vue";
import PrescriptorFilters from "@/drug-interaction/components/drug-selection/PrescriptorFilters.vue";
import {SourceType} from "@/consumer/interfaces";
import {useInterviewStore} from "@/stores/interview-store";
import {storeToRefs} from "pinia";

const props = defineProps<{
    consumerKey: string;
}>();

const {carts, cartAfterKey, loading: loadingStore} = storeToRefs(useInterviewStore());

const prescriptors = ref<number[]>([]);
const viewMode = ref<"prescriptions" | "raw">("raw");

const emits = defineEmits<{
    (e: 'launch-analysis', cips: string[]): void;
    (e: 'track-products'): void;
}>();

const {data, products, mutate} = useConsumerProducts(props.consumerKey);

const searchBarProducts = ref<LegacyProduct[]>([]);
const selectedProducts = ref<ProductSummary[]>([]);
const initialized = ref(false);

const customerId = computed(() => ConsumerKeyService.customerIdFromKey(props.consumerKey));

const initializeSelectedProducts = () => {
    if (products.value) {
        selectedProducts.value = [...products.value];
    }
};

watch(products, (newValue) => {
    if (newValue && !initialized.value) {
        initializeSelectedProducts();
        initialized.value = true;
    }
}, {immediate: true});

/**
 * Check if a product is selected.
 *
 * @param product
 */
const isSelected = (product: ProductSummary) => {
    return selectedProducts.value.some(p => p.id === product.id);
};

/**
 * Handle the selection of a product.
 *
 * @param product
 * @param source
 */
const handleSelect = async (product: ProductSummary, source: SourceType) => {
    try {
        if (isSelected(product)) {
            selectedProducts.value = selectedProducts.value.filter(p => p.id !== product.id);
        } else {
            selectedProducts.value.push(product);
        }

        const products = selectedProducts.value.map(product => {
            const consumerProduct = data.value?.find(p => p.product.id === product.id);
            const sourceType = consumerProduct ? consumerProduct.source_type : source;

            return {
                id: product.id,
                source_type: sourceType
            };
        });

        await consumerProductApi().sync(props.consumerKey, {products});
    } catch {
        initializeSelectedProducts();
    } finally {
        mutateDebounce();
    }
};

const mutateDebounce = debounce(() => {
    mutate();
}, 500);

/**
 * Handle the selection of a product from the search bar.
 *
 * @param product
 */
const handleSearchbarSelection = async (product: LegacyProduct) => {
    if (searchBarProducts.value.some(p => p.id === product.id)) {
        return;
    }

    searchBarProducts.value.push(product);

    const productSummary: ProductSummary = {
        id: product.id,
        name: product.name,
        cip: product.code,
        id_7: product.cip7
    };

    try {
        selectedProducts.value.push(productSummary);

        const products = selectedProducts.value.map(product => {
            return {
                id: product.id,
                source_type: data.value?.find(p => p.product.id === product.id)?.source_type
                    || SourceType.SELF_MEDICATION
            };
        });
        await consumerProductApi().sync(props.consumerKey, {products});
    } catch {
        selectedProducts.value = selectedProducts.value.filter(p => p.id !== product.id);
    } finally {
        mutateDebounce();
    }
};

const launchAnalysis = () => {
    if (selectedProducts.value.length) {
        emits('launch-analysis', selectedProducts.value.map(p => p.cip));
    }
};
</script>

<style scoped>
:deep(.v-selection-control .v-label) {
    flex: 1;
}
</style>
